// Public reviews showcase for the Fieldlink marketing site. Pulls published
// (4-5 star auto + admin-approved) reviews from /api/reviews/public.
import { useEffect, useState } from "react";
import { Star, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

export function Stars({ value = 0, className = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${className} ${i <= Math.round(value) ? "fill-[#FF5F15] text-[#FF5F15]" : "text-zinc-600"}`} />
      ))}
    </div>
  );
}

export function useReviews(limit = 24) {
  const [data, setData] = useState({ reviews: [], stats: { average: 0, count: 0 }, loading: true });
  useEffect(() => {
    api.get("/reviews/public", { params: { limit } })
      .then((r) => setData({ ...r.data, loading: false }))
      .catch(() => setData((d) => ({ ...d, loading: false })));
  }, [limit]);
  return data;
}

export function ReviewCard({ r }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B1018] p-6 flex flex-col" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center justify-between">
        <Stars value={r.rating} />
        {r.featured && <span className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">Featured</span>}
      </div>
      {r.title && <div className="mt-3 font-bold text-white">{r.title}</div>}
      <p className="mt-2 text-zinc-300 leading-relaxed text-[15px] flex-1">{r.body}</p>
      <div className="mt-4 text-sm text-zinc-500 font-medium">— {r.display_name}</div>
    </div>
  );
}

export function ReviewsSection() {
  const { reviews, stats, loading } = useReviews(6);
  if (loading) return null;
  return (
    <section className="bg-[#07090D] text-white py-16 lg:py-24 border-t border-white/[0.06]" data-testid="reviews-section">
      <div className="max-w-[1520px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 text-[#FF5F15]"><Quote className="w-5 h-5" /><span className="text-xs font-bold uppercase tracking-widest">From the crews</span></div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tight">What NZ civil teams say</h2>
            {stats.count > 0 && (
              <div className="mt-4 flex items-center gap-3">
                <Stars value={stats.average} className="w-5 h-5" />
                <span className="text-lg font-bold">{stats.average.toFixed(1)}</span>
                <span className="text-zinc-500">from {stats.count} review{stats.count === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>
          {reviews.length > 0 && (
            <Link to="/reviews" className="inline-flex items-center gap-2 text-sm font-bold text-[#FF5F15] hover:text-[#ff7a3d] transition-colors duration-150 w-fit" data-testid="reviews-see-all">
              Read all reviews <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {reviews.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-[#0B1018] p-10 text-center">
            <Quote className="w-8 h-8 text-[#FF5F15] mx-auto" />
            <p className="mt-4 text-zinc-300 text-lg">Fieldlink is run day-in, day-out by NZ civil crews. Reviews from our customers will appear here.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r) => <ReviewCard key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </section>
  );
}
